import React, { useCallback, useEffect, useState } from "react";
import CloseButton from "web/react/components/buttons/close-button/close-button";
import LazyModal from "web/react/components/lazy-modal/lazy-modal";
import LoadingSpinner from "web/react/components/loading-spinner/loading-spinner";
import { useToast } from "web/react/emo/toast/useToast";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { useHistoryWatcher } from "web/react/hooks/use-history-watcher/use-history-watcher";
import { useRothkoFetcher } from "web/react/hooks/use-rothko-fetcher";
import { InStockProductPage } from "web/react/pages/product/in-stock/in-stock-product-page/in-stock-product-page";
import { OutOfStockProductPage } from "web/react/pages/product/out-of-stock/out-of-stock-product-page/out-of-stock-product-page";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import { getProductPageRegex } from "web/script/routing/routes";
import history from "web/script/utils/history";
import storage from "web/script/utils/storage";
import { ProductOverlaySerializer } from "web/types/serializers";
import * as styles from "./product-overlay.css";
import { getOverlayDecision, handlePageAnalytics } from "./utils";

interface ProductOverlayProps {
    parentRef?: React.RefObject<HTMLElement>;
    isOnFeed?: boolean;
}

export function ProductOverlay({
    parentRef,
    isOnFeed = false,
}: ProductOverlayProps): React.ReactElement | null {
    const [productData, setProductData] = useState<ProductOverlaySerializer | null>(null);
    const [hasBeenClosed, setHasBeenClosed] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [pageUnderOverlayUrl, setPageUnderOverlayUrl] = useState<URL | null>(null);
    const [pagePositionOnOpen, setPagePositionOnOpen] = useState(0);

    const urlData = useHistoryWatcher();
    const { isMobileViewport, isTabletViewport } = useDomViewport();
    const [fetch, isLoading] = useRothkoFetcher("modules/product/product_overlay");
    const toast = useToast();

    const handlePagePosition = useCallback(
        (position, setState = false): void => {
            if (isMobileViewport || isTabletViewport) {
                setState && setPagePositionOnOpen(document.body.scrollTop);
                window.scrollTo({ top: position, behavior: "auto" });
            }
        },
        [isMobileViewport, isTabletViewport]
    );

    const onClose = useCallback(
        function (isBrowserPopState = false): void {
            // avoid race conditions by ensuring we only
            // close once per overlay
            if (hasBeenClosed) {
                return;
            }

            handlePagePosition(pagePositionOnOpen);
            setHasBeenClosed(true);

            // urlData may be undefined when the component is mounted
            const url = urlData ? urlData.newURL : new URL(window.location.href);

            environment.set({
                pageType: url.searchParams.get("previous_page_type"),
                pageSubType: url.searchParams.get("previous_page_sub_type"),
            });

            // we don't need to account for the actual popstate action when ifBrowserPopState is
            // true - the browser does that
            if (isBrowserPopState) {
                setIsOpen(false);
                return;
            }

            if (pageUnderOverlayUrl) {
                history.pushState(pageUnderOverlayUrl, {
                    saveScrollPosition: true,
                });
            }

            setIsOpen(false);
        },
        [hasBeenClosed, urlData, pagePositionOnOpen, handlePagePosition, pageUnderOverlayUrl]
    );

    // handle initial render
    useEffect(() => {
        // urlData may be undefined when the component is mounted
        const url = urlData ? urlData.newURL : new URL(window.location.href);
        storage.set("initialPageURL", window.location.href, true);

        if (url.hash.startsWith("#slug=")) {
            storage.set("overlayOnInitialPageLoad", true, true);

            const _pageUnderOverlayUrl = new URL(
                window.location.href.replace(window.location.hash, "")
            );
            setPageUnderOverlayUrl(_pageUnderOverlayUrl);
        } else {
            setPageUnderOverlayUrl(url);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // handle overlay state when URL changes
    useEffect(() => {
        async function fetchContent(productSlug, linkId): Promise<void> {
            // reset the previous closed state when we fetch new data
            setHasBeenClosed(false);
            setIsOpen(true);
            handlePagePosition(0, true);

            try {
                const data = (await fetch({
                    slug: productSlug,
                    link_id: linkId,
                })) as ProductOverlaySerializer;
                setProductData(data);
                handlePageAnalytics(data);
            } catch {
                onClose();
                toast({ message: gettext("error.500.title") });
            }
        }

        // urlData may be undefined when the component is mounted
        const url = urlData ? urlData.newURL : new URL(window.location.href);

        const overlayDecision = getOverlayDecision(url, urlData, isOpen, isOnFeed);

        const productSlug = url.hash.startsWith("#slug=")
            ? url.hash.replace("#slug=", "")
            : url.pathname.match(getProductPageRegex())?.[2]; // extract slug from path

        if (overlayDecision === "open") {
            fetchContent(productSlug, url.searchParams.get("link_id"));
        } else if (overlayDecision === "close") {
            onClose(true);
        } else if (overlayDecision === "reload") {
            window.location.reload();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlData]);

    useEffect(() => {
        if (isOpen) {
            document.body.classList.add("no-scroll");
        } else {
            document.body.classList.remove("no-scroll");
        }
    });

    if (!isOpen) {
        return null;
    }

    return (
        <div data-testid="overlay-container">
            <div id="product-overlay">
                <LazyModal
                    appElement={
                        document.body?.querySelector<HTMLDivElement>(".site-wrapper") || undefined
                    }
                    parentSelector={parentRef && (() => parentRef.current as HTMLElement)}
                    className={styles.content}
                    isOpen={isOpen}
                    onRequestClose={() => onClose()}
                    overlayClassName={styles.background}
                    noScroll={false}
                    shouldCloseOnOverlayClick
                    shouldCloseOnEsc
                >
                    <div className={styles.closeContainer}>
                        <CloseButton
                            useDefaultStyle={false}
                            className={styles.closeButton}
                            onClick={() => onClose()}
                        />
                    </div>
                    <div className={styles.product}>
                        {isLoading && (
                            <div data-testid="loading-spinner">
                                <LoadingSpinner />
                            </div>
                        )}
                        {!isLoading && productData?.in_stock && (
                            <div data-testid="pdp-in-stock">
                                <InStockProductPage
                                    // we need to pass the slug as a key to render a
                                    // fresh InStockProductPage each time the data changes
                                    key={productData.slug}
                                    productLayout={productData.in_stock.product_layout}
                                    productArea={productData.in_stock.product_area}
                                    isOverlay
                                />
                            </div>
                        )}
                        {!isLoading && productData?.out_of_stock && (
                            <div data-testid="pdp-out-of-stock">
                                <OutOfStockProductPage
                                    productLayout={productData.out_of_stock.product_layout}
                                    productUrl={productData.product_url}
                                    variant={
                                        productData?.out_of_stock.product_layout.is_affiliate
                                            ? "affiliate"
                                            : "icon"
                                    }
                                    isOverlay
                                    data-testid="pdp-out-of-stock"
                                />
                            </div>
                        )}
                    </div>
                </LazyModal>
            </div>
        </div>
    );
}
